import { X } from "lucide-react";
import { useState } from "react";
import { t } from "../../i18n";

export function ModelTagEditor({
  value,
  suggestions,
  disabled,
  onChange,
}: {
  value: string[];
  suggestions: string[];
  disabled: boolean;
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState("");
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const add = (raw: string) => {
    const tags = raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (tags.length) onChange([...new Set([...value, ...tags])]);
    setInput("");
    setActive(0);
  };
  const matches = input
    ? suggestions
        .filter(
          (tag) =>
            !value.includes(tag) &&
            tag.toLowerCase().includes(input.toLowerCase()),
        )
        .slice(0, 6)
    : [];
  return (
    <div className="relative">
      <div className="field flex min-h-11 flex-wrap items-center gap-1.5 p-2">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-lumen-50 px-2 py-1 text-xs font-semibold text-lumen-800 dark:bg-lumen-900/30 dark:text-lumen-200"
          >
            {tag}
            <button
              type="button"
              disabled={disabled}
              aria-label={`Remove ${tag}`}
              onClick={() => onChange(value.filter((item) => item !== tag))}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          disabled={disabled}
          value={input}
          role="combobox"
          aria-expanded={focused && matches.length > 0}
          aria-controls="tag-suggestions"
          aria-activedescendant={
            matches[active] ? `tag-suggestion-${active}` : undefined
          }
          aria-label={t("Add tag")}
          placeholder={value.length ? t("Add another…") : t("Type a tag…")}
          className="min-w-28 flex-1 bg-transparent px-1 py-0.5 text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 100)}
          onChange={(event) => {
            const next = event.target.value;
            setActive(0);
            if (next.includes(",")) add(next);
            else setInput(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && matches.length) {
              event.preventDefault();
              setActive((current) => (current + 1) % matches.length);
              return;
            }
            if (event.key === "ArrowUp" && matches.length) {
              event.preventDefault();
              setActive(
                (current) => (current - 1 + matches.length) % matches.length,
              );
              return;
            }
            if (event.key === "Escape") {
              setFocused(false);
              return;
            }
            if ((event.key === "Enter" || event.key === ",") && input.trim()) {
              event.preventDefault();
              add(
                event.key === "Enter" && matches[active]
                  ? matches[active]
                  : input,
              );
            }
            if (event.key === "Backspace" && !input && value.length)
              onChange(value.slice(0, -1));
          }}
        />
      </div>
      {focused && matches.length > 0 && (
        <div
          id="tag-suggestions"
          role="listbox"
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-stone-900"
        >
          {matches.map((tag, index) => (
            <button
              type="button"
              role="option"
              aria-selected={active === index}
              id={`tag-suggestion-${index}`}
              key={tag}
              className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${active === index ? "bg-lumen-50 text-lumen-800 dark:bg-lumen-900/30 dark:text-lumen-200" : "hover:bg-stone-100 dark:hover:bg-white/5"}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => add(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
